// Accordion — controlled collapsible section. Parent owns `open` so groups can
// enforce one-open-at-a-time. Generic disclosure primitive.
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import { CaretIcon } from '../../icons/Icons';
import './accordion.css';

export function Accordion({
  title, summary, open, onToggle, icon, toggleIcon, collapsible = true, className, children,
}) {
  const headTxt = (
    <span className="accordion__head-txt">
      <span className="accordion__title">{title}</span>
      {summary && <span className="accordion__summary">{summary}</span>}
    </span>
  );

  // A row with nothing to disclose keeps the head's layout but is not a control.
  if (!collapsible) {
    return (
      <section className={cx('accordion accordion--static', className)}>
        <div className="accordion__head">
          {icon && <span className="accordion__icon">{icon}</span>}
          {headTxt}
        </div>
      </section>
    );
  }

  return (
    <section className={cx('accordion', open && 'is-open', className)}>
      <button type="button" className="accordion__head" aria-expanded={open} onClick={onToggle}>
        {icon && <span className="accordion__icon">{icon}</span>}
        {headTxt}
        <span className="accordion__chev">
          {/* Callers can supply their own disclosure glyph (e.g. a +/− pair) — the caret
              stays the library default so existing callers are untouched. */}
          {toggleIcon ?? <CaretIcon width={16} height={16} />}
        </span>
      </button>
      {open && <div className="accordion__body">{children}</div>}
    </section>
  );
}

Accordion.propTypes = {
  title: PropTypes.node.isRequired,
  summary: PropTypes.node,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  icon: PropTypes.node,
  /** Overrides the disclosure glyph. Defaults to the rotating caret. */
  toggleIcon: PropTypes.node,
  /** False renders the head alone, as a static row with no toggle and no body. */
  collapsible: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Accordion;
